import { FieldErrors, Radio, RadioGroup } from "@certn/cascadia-react";
import { Stack } from "@certn/cascadia";
import { css } from "@emotion/css";
import {
  CascadiaJsonFormContext,
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { parseUISchemaWithElements } from "../types";
import { useHandleChange } from "../utils";

function ResidencesDateRangeControl(props: CascadiaLayoutProps) {
  const { isSummaryForPath, parentValues } = useContext(
    CascadiaJsonFormContext
  );
  const isSummary = isSummaryForPath?.(props.path);
  const { t } = useTranslation();
  const handleChange = useHandleChange();

  const otherCurrentExists =
    Array.isArray(parentValues) &&
    parentValues.some((residence) => {
      return residence !== props.data && residence.end_date == null;
    });

  const getInitialCurrentAddressState = () => {
    if (!props.data?.start_date) {
      return otherCurrentExists ? false : undefined;
    }
    return props.data?.end_date == null;
  };

  const [isCurrentAddress, setIsCurrentAddress] = useState<boolean | undefined>(
    getInitialCurrentAddressState()
  );

  const canSelectCurrent = !otherCurrentExists || isCurrentAddress === true;

  const options = props.uischema.options || {};
  const conditionalStyles =
    !isSummary && options.showDivider
      ? {
          "> *:not(:last-child)": css(styles.divider, {
            paddingBottom: "var(--space-4)",
          }),
          "&:not(:last-child)": css(styles.divider),
        }
      : undefined;

  // In rare cases, we don't want to render the summary in block format
  const displayAsBlock =
    isSummary && !options.doNotRenderSummaryInBlockFormat
      ? { display: "block" }
      : undefined;

  const uischema = parseUISchemaWithElements(props.uischema);

  const uiSchemaWithoutEndDate = {
    ...uischema,
    type: "VerticalLayout" as const,
    elements:
      uischema?.elements?.filter(
        (element: any) => element.name !== "end_date"
      ) || [],
  };

  return (
    <Stack>
      <RadioGroup
        label={t("Is this your current address?")}
        orientation="horizontal"
        onChange={(value) => {
          const newIsCurrentAddress = value === "true";
          setIsCurrentAddress(newIsCurrentAddress);

          if (newIsCurrentAddress) {
            handleChange("end_date", undefined);
          }
        }}
        value={
          isCurrentAddress === true
            ? "true"
            : isCurrentAddress === false
              ? "false"
              : otherCurrentExists
                ? "false"
                : undefined
        }
        isRequired
      >
        <Radio
          testID="address-history-start-date-radio"
          value="true"
          isDisabled={!canSelectCurrent}
        >
          {t("Yes")}
        </Radio>
        <Radio testID="address-history-end-date-radio" value="false">
          {t("No")}
        </Radio>
      </RadioGroup>
      {isCurrentAddress !== undefined && (
        <BaseLayout
          {...props}
          uischema={isCurrentAddress ? uiSchemaWithoutEndDate : props.uischema}
          testID="vertical-layout"
          className={css(
            conditionalStyles,
            displayAsBlock,
            options.override_gap && { gap: "var(--space-2)" }
          )}
        />
      )}
      {props.touched && <FieldErrors>{props.errors}</FieldErrors>}
    </Stack>
  );
}

const styles = {
  divider: css({
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "var(--border-base)",
  }),
};

ResidencesDateRangeControl.displayName = "ResidencesDateRangeControl";

export const ResidencesDateRangeControlRenderer = withCascadiaLayoutProps(
  ResidencesDateRangeControl
);
